/* Karaoke Studio — saved songs, key suggestion, session score.
 *
 * Pure logic only: storage shape, the transpose maths and the session summary.
 * Every DOM wire lives in karaoke-app.js, so all of this is unit-testable
 * (tests/js/karaoke-session.test.mjs).
 *
 * WHY NO AUDIO IS STORED
 * A saved song keeps settings, lyrics and the analysis — not the track. A song
 * is 5–50 MB; localStorage cannot hold it and IndexedDB would spend the user's
 * whole quota on a few files and then start failing in ways they cannot see.
 * The file is the one thing they already have on disk, so re-picking it is
 * cheap; what is expensive to redo is the tuning and the lyric timing.
 */
(function () {
    'use strict';

    var STORE_KEY = 'timbrica_karaoke_songs';
    var RANGE_KEY = 'timbrica_vocal_range_history';   // written by /vocal-range
    var MAX_SONGS = 20;
    var SCHEMA = 1;

    // ── Storage ─────────────────────────────────────────────────────────────
    function listSongs() {
        try {
            var raw = JSON.parse(localStorage.getItem(STORE_KEY));
            if (!Array.isArray(raw)) return [];
            return raw.filter(function (s) { return s && s.id && s.v === SCHEMA; });
        } catch (_) { return []; }
    }

    // Same file name + duration = the same song being re-tuned, not a new one.
    // Without this, five practice sessions on one track fill the whole list.
    function songKey(entry) {
        return String(entry.name || '') + '|' + Math.round(entry.dur || 0);
    }

    function saveSong(entry) {
        if (!entry || !entry.name) return null;
        var list = listSongs();
        var rec = {
            v: SCHEMA,
            id: entry.id || (songKey(entry) + '|' + (entry.savedAt || '')),
            savedAt: entry.savedAt || '',
            name: String(entry.name).slice(0, 120),
            dur: entry.dur || 0,
            bpm: entry.bpm || 0,
            musicKey: entry.musicKey || '',
            mode: entry.mode || 'fast',
            keyShift: entry.keyShift || 0,
            tempo: entry.tempo || 100,
            guide: entry.guide == null ? 10 : entry.guide,
            reverb: entry.reverb || 'off',
            lyrics: String(entry.lyrics || '').slice(0, 20000),
            lyricsMode: entry.lyricsMode === 'lrc' ? 'lrc' : 'plain',
            bestScore: entry.bestScore || 0,
        };
        var k = songKey(rec);
        var out = list.filter(function (s) { return songKey(s) !== k; });
        out.unshift(rec);
        if (out.length > MAX_SONGS) out.length = MAX_SONGS;
        try {
            localStorage.setItem(STORE_KEY, JSON.stringify(out));
        } catch (_) {
            // Quota or private mode: drop the oldest half and try once more so
            // saving degrades instead of throwing in the user's face.
            try {
                out.length = Math.max(1, Math.floor(out.length / 2));
                localStorage.setItem(STORE_KEY, JSON.stringify(out));
            } catch (_e) { return null; }
        }
        return rec;
    }

    function deleteSong(id) {
        var out = listSongs().filter(function (s) { return s.id !== id; });
        try { localStorage.setItem(STORE_KEY, JSON.stringify(out)); } catch (_) {}
        return out;
    }

    function findSong(name, dur) {
        var k = String(name || '') + '|' + Math.round(dur || 0);
        var hit = listSongs().filter(function (s) { return songKey(s) === k; });
        return hit.length ? hit[0] : null;
    }

    // ── Vocal range ─────────────────────────────────────────────────────────
    function hzToMidi(hz) { return 69 + 12 * Math.log(hz / 440) / Math.LN2; }

    var NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    function midiToName(m) {
        var r = Math.round(m);
        return NOTE_NAMES[((r % 12) + 12) % 12] + (Math.floor(r / 12) - 1);
    }

    /**
     * The sung range of a track, from PitchTracker frames.
     * Percentiles, not min/max: an octave-error frame or one breath artefact
     * would otherwise stretch the range by a fifth and ruin the suggestion.
     */
    function rangeFromFrames(frames, opts) {
        opts = opts || {};
        var minConf = opts.minConf == null ? 0.8 : opts.minConf;
        var lowPct = opts.lowPct == null ? 0.1 : opts.lowPct;
        var highPct = opts.highPct == null ? 0.9 : opts.highPct;
        var midi = [];
        for (var i = 0; i < (frames || []).length; i++) {
            var f = frames[i];
            if (!f || !(f.freqHz > 0)) continue;
            if (f.confidence != null && f.confidence < minConf) continue;
            if (f.freqHz < 60 || f.freqHz > 1600) continue;   // outside any sung voice
            midi.push(hzToMidi(f.freqHz));
        }
        if (midi.length < 20) return null;                     // too little to trust
        midi.sort(function (a, b) { return a - b; });
        var at = function (p) { return midi[Math.min(midi.length - 1, Math.max(0, Math.round(p * (midi.length - 1))))]; };
        return { low: at(lowPct), high: at(highPct), frames: midi.length };
    }

    /** Latest /vocal-range measurement, or null if the user never took one. */
    function readUserRange() {
        try {
            var hist = JSON.parse(localStorage.getItem(RANGE_KEY));
            if (!Array.isArray(hist) || !hist.length) return null;
            var h = hist[0];
            if (!(h.low > 0) || !(h.high > h.low)) return null;
            return { low: h.low, high: h.high, voiceType: h.voiceType || '' };
        } catch (_) { return null; }
    }

    /**
     * Semitones to transpose so the song's vocal sits inside the singer's range.
     *
     * Centre-to-centre, clamped to the tool's ±12. Centring rather than "just
     * clear the ceiling" because a singer strains at BOTH ends — dropping a song
     * until the high note is comfortable can bury the verses under their floor.
     * Returns { shift, fits, songLow.., userLow.. } with names ready for display.
     */
    function suggestKeyShift(songRange, userRange, opts) {
        if (!songRange || !userRange) return null;
        var limit = (opts && opts.limit) || 12;
        var songCentre = (songRange.low + songRange.high) / 2;
        var userCentre = (userRange.low + userRange.high) / 2;
        var raw = userCentre - songCentre;
        var shift = Math.max(-limit, Math.min(limit, Math.round(raw)));
        var lo = songRange.low + shift;
        var hi = songRange.high + shift;
        return {
            shift: shift,
            fits: lo >= userRange.low - 0.5 && hi <= userRange.high + 0.5,
            songLow: songRange.low, songHigh: songRange.high,
            songLowName: midiToName(songRange.low), songHighName: midiToName(songRange.high),
            userLowName: midiToName(userRange.low), userHighName: midiToName(userRange.high),
            shiftedLowName: midiToName(lo), shiftedHighName: midiToName(hi),
            voiceType: userRange.voiceType || '',
        };
    }

    // ── Session score ───────────────────────────────────────────────────────
    /**
     * Roll up the takes of one sitting. `best` is what people quote, `last`
     * is what they just did, `trend` is what makes practice feel like progress.
     */
    function sessionSummary(attempts) {
        var list = (attempts || []).filter(function (a) { return a && a.score >= 0; });
        if (!list.length) return null;
        var scores = list.map(function (a) { return a.score; });
        var best = Math.max.apply(null, scores);
        var last = scores[scores.length - 1];
        var first = scores[0];
        var sum = scores.reduce(function (a, b) { return a + b; }, 0);
        return {
            takes: list.length,
            best: best,
            last: last,
            avg: Math.round(sum / list.length),
            trend: list.length > 1 ? last - first : 0,
            grade: best >= 90 ? 'A' : best >= 75 ? 'B' : best >= 60 ? 'C' : 'D',
        };
    }

    window.KaraokeSession = {
        MAX_SONGS: MAX_SONGS,
        listSongs: listSongs,
        saveSong: saveSong,
        deleteSong: deleteSong,
        findSong: findSong,
        rangeFromFrames: rangeFromFrames,
        readUserRange: readUserRange,
        suggestKeyShift: suggestKeyShift,
        sessionSummary: sessionSummary,
        hzToMidi: hzToMidi,
        midiToName: midiToName,
    };
})();
